// Tiny shared API client for the booking + admin flows.
export const API = process.env.REACT_APP_BACKEND_URL;

const ADMIN_TOKEN_KEY = "gaffer-admin-token";

export function getAdminToken() {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}

export function setAdminToken(token) {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

async function handle(res) {
    if (!res.ok) {
        let detail = `Request failed (${res.status})`;
        try {
            const data = await res.json();
            if (data?.detail) detail = data.detail;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    return res.json();
}

export async function fetchAvailability(date) {
    const res = await fetch(
        `${API}/api/bookings/availability?date=${encodeURIComponent(date)}`
    );
    return handle(res);
}

export async function submitBooking(payload) {
    const res = await fetch(`${API}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return handle(res);
}

export async function adminLogin(password) {
    const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
    });
    const data = await handle(res);
    setAdminToken(data.token);
    return data;
}

export async function fetchAdminBookings(statusFilter = "all") {
    const res = await fetch(
        `${API}/api/admin/bookings?status_filter=${encodeURIComponent(statusFilter)}`,
        {
            headers: { Authorization: `Bearer ${getAdminToken()}` },
        }
    );
    return handle(res);
}

export async function confirmBooking(id) {
    const res = await fetch(`${API}/api/admin/bookings/${id}/confirm`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

export async function declineBooking(id) {
    const res = await fetch(`${API}/api/admin/bookings/${id}/decline`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

export async function deleteBooking(id) {
    const res = await fetch(`${API}/api/admin/bookings/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

// ---- Analytics ---------------------------------------------------------
// Fire-and-forget tracking. Silently swallows errors so blocked requests
// never crash the UI. Skips tracking when the user is on the /admin route
// (so the owner viewing the dashboard doesn't inflate their own metrics).
export function trackEvent(event_type, target) {
    if (typeof window !== "undefined") {
        if (window.location.pathname.startsWith("/admin")) return;
    }
    try {
        fetch(`${API}/api/analytics/track`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event_type, target }),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

export async function fetchAdminAnalytics() {
    const res = await fetch(`${API}/api/admin/analytics`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

export async function adminMe() {
    const res = await fetch(`${API}/api/admin/me`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

// ---- Vouchers (admin) --------------------------------------------------
export async function fetchAdminVouchers() {
    const res = await fetch(`${API}/api/admin/vouchers`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return handle(res);
}

export async function redeemVoucher(code, amount) {
    const res = await fetch(
        `${API}/api/admin/vouchers/${encodeURIComponent(code)}/redeem`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getAdminToken()}`,
            },
            body: JSON.stringify({ amount }),
        }
    );
    return handle(res);
}

export async function voidVoucher(code) {
    const res = await fetch(
        `${API}/api/admin/vouchers/${encodeURIComponent(code)}/void`,
        {
            method: "POST",
            headers: { Authorization: `Bearer ${getAdminToken()}` },
        }
    );
    return handle(res);
}

export async function undoVoucher(code) {
    const res = await fetch(
        `${API}/api/admin/vouchers/${encodeURIComponent(code)}/undo`,
        {
            method: "POST",
            headers: { Authorization: `Bearer ${getAdminToken()}` },
        }
    );
    return handle(res);
}
